import { calculateIncrease } from "@pages/AdsAnalitics/components/Campaigns/utils";
import { AdsSalesApi } from "./ads-sales/ads-sales.api";
import { WidgetMetricKey, WidgetMetrics } from "./math/math.service";
import { IWidget } from "@pages/Dashboard/types/dashboard.types";
import { WIDGET_METRICS_CONFIG, WidgetConfigItem } from "./math/math.const";
import { SalesPerformanceService } from "./sales-performance/sales-performance.service";
import { OrganicApi } from "./organic/organic.api";
import { CfoApi } from "./cfo/cfo.api";
import { TotalSalesApi } from "./total-sales/total-sales.api";

export interface ChartData {
  date: string;
  value: number;
}

interface ExtendedWidgetData extends IWidget {
  chartData?: ChartData[];
}

interface GetWidgetsDataParams {
  selectedKeys: WidgetMetricKey[];
  dateStart: string;
  dateEnd: string;
  compareStart?: string;
  compareEnd?: string;
  compareType?: "raw" | "percent";
  includeChartData?: boolean;
  saturation?: number;
  saturationTab?: string;
  saturationKeys?: string[];
  asin?: string;
}

interface GetWidgetsMetricsParams {
  dateStart: string;
  dateEnd: string;
  compareStart?: string;
  compareEnd?: string;
  saturation?: number;
  tab?: string;
  saturationKeys?: string[];
  asin?: string;
}
const pad = (n: number) => String(n).padStart(2, "0");

const isoLocal = (d: Date | string) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  return [dt.getFullYear(), pad(dt.getMonth() + 1), pad(dt.getDate())].join(
    "-",
  );
};

const ttl = 60000;

export class WidgetClass {
  private ads = new AdsSalesApi();
  private org = new OrganicApi();
  private cfo = new CfoApi();
  private tot = new TotalSalesApi();
  private perf = new SalesPerformanceService();

  private static coreCache = new Map<
    string,
    Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }
  >();
  private static coreInFlight = new Map<
    string,
    Promise<Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }>
  >();

  private key = (p: GetWidgetsMetricsParams) =>
    JSON.stringify({
      ds: isoLocal(p.dateStart),
      de: isoLocal(p.dateEnd),
      cs: p.compareStart ? isoLocal(p.compareStart) : "",
      ce: p.compareEnd ? isoLocal(p.compareEnd) : "",
      a: p.asin ?? "",
    });

  async getWidgetsData(p: GetWidgetsDataParams): Promise<ExtendedWidgetData[]> {
    const core = await this.getCoreMetrics({
      dateStart: p.dateStart,
      dateEnd: p.dateEnd,
      compareStart: p.compareStart,
      compareEnd: p.compareEnd,
      asin: p.asin,
    });
    const charts = p.includeChartData
      ? await this.loadCharts(
          p.selectedKeys,
          new Date(p.dateStart),
          new Date(p.dateEnd),
        )
      : [];
    return p.selectedKeys.map((k, i) => {
      const { value, diff } = this.val(core, k, p.compareType ?? "raw");
      return {
        id: k,
        name: WidgetClass.name(k),
        value: value.toString(),
        originalValue: value,
        difference: diff,
        key: k,
        chartData: p.includeChartData ? charts[i] : undefined,
      };
    });
  }

  private async getCoreMetrics(p: GetWidgetsMetricsParams) {
    const k = this.key(p);
    if (WidgetClass.coreCache.has(k)) return WidgetClass.coreCache.get(k)!;
    if (WidgetClass.coreInFlight.has(k))
      return WidgetClass.coreInFlight.get(k)!;
    const prom = this.fetchCore(p, k);
    WidgetClass.coreInFlight.set(k, prom);
    const res = await prom;
    WidgetClass.coreInFlight.delete(k);
    return res;
  }

  private async fetchCore(p: GetWidgetsMetricsParams, k: string) {
    const { dateStart, dateEnd, compareStart, compareEnd, asin } = p;
    const [org, cfoRaw, ads, totRaw] = await Promise.all([
      this.org.getOrganicMetrics(dateStart, dateEnd, asin),
      this.cfo.getExecutiveSummary(dateStart, dateEnd, asin).catch(() => ({})),
      this.ads
        .getAllMetrics(dateStart, dateEnd, 0, asin)
        .then((r) => (Array.isArray(r) ? r[0] : r)),
      this.tot.getAll(dateStart, dateEnd, asin).catch(() => ({})),
    ]);
    const base = this.map({ ...org, ...cfoRaw, ...ads, ...totRaw });
    if (compareStart && compareEnd) {
      const cmpRaw = await this.ads.getWidgetsComparePromise(
        dateStart,
        dateEnd,
        compareStart,
        compareEnd,
      );
      base.compare = this.map(cmpRaw);
    }
    WidgetClass.coreCache.set(k, base);
    setTimeout(() => WidgetClass.coreCache.delete(k), ttl);
    return base;
  }

  public async getWidgetsMetrics(
    params: GetWidgetsMetricsParams,
  ): Promise<Partial<WidgetMetrics> & { compare?: Partial<WidgetMetrics> }> {
    const {
      dateStart,
      dateEnd,
      compareStart,
      compareEnd,
      saturation = 0,
      asin,
    } = params;
    const raw = await this.ads
      .getAllMetrics(dateStart, dateEnd, saturation, asin)
      .then((r) => (Array.isArray(r) ? r[0] : r));
    const organic = await this.org.getOrganicMetrics(dateStart, dateEnd, asin);
    const cfo = await this.cfo
      .getExecutiveSummary(dateStart, dateEnd, asin)
      .catch(() => ({}));
    const total = await this.tot
      .getAll(dateStart, dateEnd, asin)
      .catch(() => ({}));
    const metrics = this.adaptApiResponseToWidgetMetrics({
      ...raw,
      ...organic,
      ...cfo,
      ...total,
    });
    if (compareStart && compareEnd) {
      const cmp = await this.ads.getWidgetsComparePromise(
        dateStart,
        dateEnd,
        compareStart,
        compareEnd,
      );
      return { ...metrics, compare: this.adaptApiResponseToWidgetMetrics(cmp) };
    }
    return metrics;
  }

  private map(data: any): Partial<WidgetMetrics> {
    const res: Partial<WidgetMetrics> = {};
    Object.entries(data).forEach(([k, v]) => {
      let c = k.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
      if (k.startsWith("ad_"))
        c = "ad" + c.charAt(2).toUpperCase() + c.slice(3);
      else if (["roas", "acos", "cpc", "cpm", "ctr", "cvr", "cac"].includes(k))
        c = "ad" + k.charAt(0).toUpperCase() + k.slice(1);
      res[c as keyof WidgetMetrics] = v as number;
    });
    return res;
  }

  private val(m: any, k: WidgetMetricKey, t: "raw" | "percent") {
    const b = k.startsWith("ad") && !m[k] ? k.slice(2) : k;
    const current =
      b === "totalSales"
        ? (m.totalSales ?? 0)
        : b === "contribution"
          ? (m.contribution ?? 0)
          : (m[b] ?? 0);
    if (!m.compare) return { value: current, diff: "0" };
    const prev =
      b === "totalSales"
        ? (m.compare.totalSales ?? 0)
        : b === "contribution"
          ? (m.compare.contribution ?? 0)
          : (m.compare[b] ?? 0);
    const diff =
      t === "raw"
        ? current - prev > 0
          ? `+${current - prev}`
          : `${current - prev}`
        : calculateIncrease(current, prev).increase;
    return { value: current, diff };
  }

  private async loadCharts(keys: WidgetMetricKey[], s: Date, e: Date) {
    return Promise.all(
      keys.map(async (k) => {
        const mk = WIDGET_METRICS_CONFIG[k] ? k : this.camel(k);
        try {
          return (await this.perf.fetchMetricData(mk, iso(s), iso(e))).map(
            (v, i) => ({
              date: iso(new Date(Date.now() - i * 86400000)),
              value: v,
            }),
          );
        } catch {
          return [];
        }
      }),
    );
  }

  private camel = (k: string) =>
    k.toLowerCase().replace(/_([a-z])/g, (_, l) => l.toUpperCase());

  private getMetricFromConfig(key: string): WidgetConfigItem {
    return WIDGET_METRICS_CONFIG[key] ?? WIDGET_METRICS_CONFIG[this.camel(key)];
  }

  public static name(k: string) {
    return WIDGET_METRICS_CONFIG[k]?.label;
  }

  formatValue = (k: string, v: number) => {
    const cfg = this.getMetricFromConfig(k);
    if (k === "adCac")
      return v < 0 ? `-$${(-v).toFixed(2)}` : `$${v.toFixed(2)}`;
    if (!cfg?.format) return Math.floor(v).toLocaleString("en-US");
    const n = Math.abs(v),
      neg = v < 0;
    switch (cfg.format) {
      case "currency":
        return neg
          ? `-$${Math.floor(n).toLocaleString("en-US")}`
          : `$${Math.floor(v).toLocaleString("en-US")}`;
      case "percent-decimal":
        return `${neg ? "-" : ""}${(n < 1 ? n * 100 : n).toFixed(2)}%`;
      case "percent":
        return `${neg ? "-" : ""}${Math.floor(n < 1 ? n * 100 : n)}%`;
      case "number":
        return `${neg ? "-" : ""}${Math.floor(n).toLocaleString("en-US")}`;
      case "decimal":
        return v.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      case "currency-decimal":
        return neg
          ? `-$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      default:
        return `${neg ? "-" : ""}${Math.floor(n).toLocaleString("en-US")}`;
    }
  };

  private readonly convertToCamelCase = (key: string): string => {
    const baseKey = key.toLowerCase().replace(/_what_if$/, "");
    const specialCases: Record<string, string> = {
      roas: "adRoas",
      acos: "adAcos",
      cpc: "adCpc",
      cpm: "adCpm",
    };
    if (specialCases[baseKey]) return specialCases[baseKey];
    return baseKey.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
  };

  private adaptApiResponseToWidgetMetrics(
    data: any,
    useWhatIf = false,
  ): Partial<WidgetMetrics> {
    if (!data) return {};
    const result: Partial<WidgetMetrics> = {};
    const keyMap = new Map<string, any>();
    Object.entries(data).forEach(([k, v]) => keyMap.set(k, v));
    for (const [k, v] of Object.entries(data)) {
      if (!useWhatIf && k.endsWith("_what_if")) continue;
      if (useWhatIf && !k.endsWith("_what_if") && keyMap.has(`${k}_what_if`))
        continue;
      const proc =
        useWhatIf && k.endsWith("_what_if") ? k.replace("_what_if", "") : k;
      let norm = proc.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
      if (proc.startsWith("ad_"))
        norm = "ad" + norm.charAt(2).toUpperCase() + norm.slice(3);
      else if (
        ["roas", "acos", "cpc", "cpm", "ctr", "cvr", "cac"].includes(proc)
      )
        norm = "ad" + proc.charAt(0).toUpperCase() + proc.slice(1);
      result[norm as keyof WidgetMetrics] = v as number;
    }
    return result;
  }

  public static convertKeyToName(key: string): string {
    return WIDGET_METRICS_CONFIG[key]?.label;
  }
}

export const widgetService = new WidgetClass();
